/**
 * Auth state atoms with localStorage persistence via Jotai's atomWithStorage.
 *
 * All keys use the `bw_` prefix to avoid collision with bubot_admin, which
 * runs on the same domain during local development.
 *
 * `isAuthenticatedAtom` is a derived (read-only) atom — it recomputes
 * automatically when either the token or user atom changes.
 * @module authAtoms
 */

import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'

// getOnInit: true reads localStorage synchronously on first atom read so
// `isAuthenticated` is correct on the very first render. Without this,
// Jotai's default async hydration leaves the atoms momentarily `null` after
// a page refresh, which makes ProtectedRoute bounce a logged-in user to
// /login before localStorage has been consulted.
const STORAGE_OPTS = { getOnInit: true }

/** @type {import('jotai').WritableAtom<string|null>} JWT token, persisted to localStorage */
export const authTokenAtom = atomWithStorage('bw_authToken', null, undefined, STORAGE_OPTS)

/** @type {import('jotai').WritableAtom<Object|null>} Current user object, persisted to localStorage */
export const authUserAtom = atomWithStorage('bw_authUser', null, undefined, STORAGE_OPTS)

/** @type {import('jotai').Atom<boolean>} Derived from authUserAtom — true when user.provisional is true */
export const isProvisionalAtom = atom(
  (get) => {
    const user = get(authUserAtom)
    return !!user && user.provisional === true
  }
)

/** @type {import('jotai').WritableAtom<boolean>} Set to true once atoms have rehydrated from localStorage */
export const authHydratedAtom = atom(false)

/**
 * Stories the current user can facilitate (release sessions for), surfaced by
 * the backend UserSerializer as `facilitated_stories` ([{ id, name }]). Derived
 * from the user's group-scoped :facilitator role. Empty for non-facilitators.
 * @type {import('jotai').Atom<Array<{id: string, name: string}>>}
 */
export const facilitatedStoriesAtom = atom(
  (get) => get(authUserAtom)?.facilitated_stories ?? []
)

/** @type {import('jotai').Atom<boolean>} True when the user facilitates at least one story */
export const isFacilitatorAtom = atom(
  (get) => get(facilitatedStoriesAtom).length > 0
)

/**
 * Groups the current user facilitates ([{ id, name }]), from the backend
 * UserSerializer `facilitated_groups`. The group-scoped facilitator surface
 * (/facilitate/group/:id) is keyed on this. Empty for non-facilitators.
 * @type {import('jotai').Atom<Array<{id: number, name: string}>>}
 */
export const facilitatedGroupsAtom = atom(
  (get) => get(authUserAtom)?.facilitated_groups ?? []
)

/** @type {import('jotai').Atom<boolean>} Derived atom — true when both token and user are present */
export const isAuthenticatedAtom = atom(
  (get) => !!get(authTokenAtom) && !!get(authUserAtom)
)
